/**
 * Shared flow-control gate for interval-based polling.
 *
 * A 429 means the whole client is over budget right now, not just the one query that hit
 * it. This app runs a dozen independent `refetchInterval` pollers (site health, alarms,
 * device counts, write requests, …), each on its own timer with no shared awareness of the
 * others — so retrying just the one query that got a 429, on its own schedule, while every
 * other poller keeps firing on ITS schedule, does not recover: the server sees the same
 * total request rate, still over the limit, for as long as the retries and the regular
 * intervals keep landing in the same window. That is how a brief brush with a rate limit
 * turns into a sustained one.
 *
 * So a 429 anywhere pauses every poller that reads this gate, for the window the server
 * actually asked for (`Retry-After`) rather than a guessed backoff — see `markRateLimited`,
 * called from the `QueryCache`'s global `onError` in `app/providers.tsx` so it fires no
 * matter which hook's request got refused.
 *
 * `document.hidden` pauses them too, for the ordinary reason: nobody is looking at a
 * backgrounded tab, so there is nothing to keep fresh.
 *
 * A third gate lives here too: `tab-sync.ts`'s `recentlySeenElsewhere`, for the OTHER way
 * polling multiplies — several open tabs of the same user, each running this exact interval
 * independently. See that module for why it is a per-round race rather than a leader.
 */
import { recentlySeenElsewhere } from './tab-sync';

let rateLimitedUntil = 0;

/** Called once, from the query cache's global error handler. */
export function markRateLimited(retryAfterMs: number): void {
  rateLimitedUntil = Math.max(rateLimitedUntil, Date.now() + retryAfterMs);
}

/** For tests: force the gate back open. */
export function clearRateLimit(): void {
  rateLimitedUntil = 0;
}

/**
 * Every query key a `pollInterval` has ever been asked about, serialised. `QueryCache`'s
 * `onSuccess` in `app/providers.tsx` reads this to decide which fetches are worth telling
 * other tabs about — `query.options` at the cache level does not carry `refetchInterval`
 * (that option is an observer concern, not a cache-level one), so there is no field on the
 * query itself to check instead.
 */
const polledKeys = new Set<string>();

/**
 * Wrap a fixed cadence so it honours the shared gate. Pass the RESULT to `refetchInterval` —
 * TanStack Query calls this on every tick, not just once, so it re-evaluates the gate live
 * rather than freezing whatever `document.hidden` was at mount. Takes the query itself (not
 * just the interval) because the cross-tab check needs its `queryKey`.
 */
export function pollInterval(baseMs: number): (query: { queryKey: readonly unknown[] }) => number | false {
  return (query) => {
    polledKeys.add(JSON.stringify(query.queryKey));
    if (typeof document !== 'undefined' && document.hidden) return false;
    if (Date.now() < rateLimitedUntil) return false;
    // 0.9: leaves room for two tabs' timers to be slightly out of phase without either one
    // missing a round entirely — see `tab-sync.ts`.
    if (recentlySeenElsewhere(query.queryKey, baseMs * 0.9)) return false;
    return baseMs;
  };
}

/** True once some `pollInterval`-wrapped hook has mounted with this exact key. */
export function isPolledQuery(queryKey: readonly unknown[]): boolean {
  return polledKeys.has(JSON.stringify(queryKey));
}
